import type { SanityQuizBlock } from '@lib/sanity/types';

/** GYTR v2 step blocks — used to detect a GYTR step flow. */
export const GYTR_STEP_BLOCK_TYPES = new Set([
  'gytrImportStepBlock',
  'gytrManualEntryStepBlock',
  'gytrEmailGateBlock',
  'gytrProcessingBlock',
  'gytrRevealBlock',
  'gytrTransferBlock'
]);

// Unlike the readiness flow, the GYTR paywall is step 6 and carries its own
// progress pip (see the v2 mocks: six pips, the last one the paywall).
const GYTR_PROGRESS_BLOCK_TYPES = new Set([
  ...GYTR_STEP_BLOCK_TYPES,
  'paywallBlock'
]);

export const isGytrStepFlow = (blocks: SanityQuizBlock[]): boolean =>
  blocks.some((block) => GYTR_STEP_BLOCK_TYPES.has(block._type));

// The build-manually step (1b) is a branch of the import step, so the two
// collapse into a single pip — selecting "Build manually" doesn't advance
// the progress bar.
const PROGRESS_GROUP_BY_TYPE: Record<string, string> = {
  gytrImportStepBlock: 'import',
  gytrManualEntryStepBlock: 'import'
};

export type GytrProgressStep = {
  groupId: string;
  blockIndexes: number[];
};

/**
 * Derives the progress pips programmatically from the page's blocks array:
 * one pip per step, with the import + build-manually branch collapsed into
 * one. The trailing paywall block carries its own pip.
 */
export const deriveProgressSteps = (
  blocks: SanityQuizBlock[]
): GytrProgressStep[] => {
  const steps: GytrProgressStep[] = [];

  blocks.forEach((block, index) => {
    if (!GYTR_PROGRESS_BLOCK_TYPES.has(block._type)) return;

    const groupId = PROGRESS_GROUP_BY_TYPE[block._type] ?? block._type;
    const previous = steps[steps.length - 1];

    if (previous && previous.groupId === groupId) {
      previous.blockIndexes.push(index);
    } else {
      steps.push({ groupId, blockIndexes: [index] });
    }
  });

  return steps;
};

/**
 * The pip index for the currently active block, or -1 when the active block
 * carries no pip.
 */
export const getCurrentProgressStep = (
  steps: GytrProgressStep[],
  currentBlockIndex: number
): number =>
  steps.findIndex((step) => step.blockIndexes.includes(currentBlockIndex));
